import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IconPlusCircle } from "../../icons/icons";
import { cleanSymbol, shortName, instrumentName } from "./watchlistFormat";
import { stockSearch, getWatchlistItems } from "../../api/user";
import SegmentedAssetSearch from "./SegmentedAssetSearch";
import AssetAvatar from "./AssetAvatar";
import ListSkeleton from "../Skeleton/ListSkeleton";

// Add-Assets mode. Mirrors the old layout's two API calls:
//   • browse — getWatchlistItems("stocks"|"crypto"|"forex") for the picked
//     segment (the default category list shown before typing)
//   • search — stockSearch({category, query}) when the user types
// Tapping + adds the asset's symbol to the current list.
const CATEGORY_KEY = { Stocks: "stocks", Crypto: "crypto", Forex: "forex" };
const BROWSE_LIMIT = 3; // small pages load fast (each row is price-enriched); "Load more" fetches 3 more

export default function AddAssetsPanel({ listName, onClose, onAdd }) {
  const [category, setCategory] = useState("Stocks");
  const [query, setQuery] = useState("");

  const [browseItems, setBrowseItems] = useState([]);
  const [browsePage, setBrowsePage] = useState(1);
  const [browseHasMore, setBrowseHasMore] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);

  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const controllerRef = useRef(null);
  const debounceRef = useRef(null);
  const browseReqRef = useRef(0);

  const trimmedQuery = query.trim();
  const isSearchMode = trimmedQuery.length > 0;

  // Browse: default category list (same call the old layout used on tile select).
  const loadBrowse = useCallback(async (cat, page) => {
    const key = CATEGORY_KEY[cat] || "stocks";
    const reqId = ++browseReqRef.current;
    setBrowseLoading(true);
    try {
      const r = await getWatchlistItems(key, page, BROWSE_LIMIT);
      if (reqId !== browseReqRef.current) return;
      const its = Array.isArray(r?.data?.items) ? r.data.items : [];
      setBrowseItems((prev) => (page === 1 ? its : [...prev, ...its]));
      setBrowsePage(page);
      setBrowseHasMore(r?.data?.pagination?.has_more ?? its.length === BROWSE_LIMIT);
    } catch (e) {
      if (reqId === browseReqRef.current) {
        console.error("Failed to load assets", e);
        if (page === 1) setBrowseItems([]);
      }
    } finally {
      if (reqId === browseReqRef.current) setBrowseLoading(false);
    }
  }, []);

  // Reset + reload browse whenever the segment changes (and on mount).
  useEffect(() => {
    setBrowseItems([]);
    setBrowsePage(1);
    setBrowseHasMore(false);
    loadBrowse(category, 1);
  }, [category, loadBrowse]);

  // Debounced, cancellable search when a query is present.
  useEffect(() => {
    if (!trimmedQuery) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Mark the search busy from the FIRST keystroke, not when the debounce
    // fires — otherwise those 400ms render as "No matches found".
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      if (controllerRef.current) controllerRef.current.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        const res = await stockSearch({ category, query: trimmedQuery }, controller.signal);
        setSearchResults(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        if (err?.name !== "CanceledError") {
          console.error("Asset search failed:", err);
          setSearchResults([]);
        }
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [trimmedQuery, category]);

  const navigate = useNavigate();
  // Clicking the asset (avatar + name) opens its chart, mirroring the watchlist
  // AssetRow: stash the ticker and route to /chartSearch/:symbol. The + button
  // still adds the asset to the list.
  const openChart = (sym) => {
    if (!sym) return;
    localStorage.setItem("ticker", sym);
    navigate(`/chartSearch/${sym}`);
  };

  // Normalize a row from either source into { chip, logo, name, addSymbol }.
  const displayFor = (item) => {
    const catKey = CATEGORY_KEY[category] || "stocks";
    if (item.ticker) {
      // Search-result shape. Chip is the BASE symbol only (never the raw pair,
      // so "X:BTCUSD" shows "BTC" not "BTCU…"); title is the title-cased name.
      // No brand art exists for provider search hits — the chip stands in.
      const chip = cleanSymbol(item.ticker, catKey);
      return {
        chip,
        logo: null,
        name: instrumentName({ name: item.name, symbol: item.ticker, category: catKey }),
        addSymbol: item.ticker,
      };
    }
    const chip = cleanSymbol(item.symbol);
    const name = item.details?.stock_name
      ? shortName(item.details.stock_name)
      : item.watchlist_name || chip;
    return {
      chip,
      // Curated rows carry hand-picked brand art; prefer it over the initials.
      logo: item.logo || null,
      name,
      addSymbol: item.details?.stock_symbol || item.symbol,
    };
  };

  const rows = isSearchMode ? searchResults : browseItems;
  const loading = isSearchMode ? searching : browseLoading;

  return (
    <div>
      {/* Header */}
      <div className="mb-1 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#004d4d] md:text-3xl">
            Add Assets to {listName}
          </h2>
          <p className="mt-1 text-sm text-secondary">Search and add assets to this list</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-sm font-medium text-secondary transition-colors hover:text-primaryColor"
        >
          Close
        </button>
      </div>

      <div className="mt-5">
        <SegmentedAssetSearch
          category={category}
          onCategoryChange={setCategory}
          query={query}
          onQueryChange={setQuery}
          // "Searching" belongs to an actual search — a browse load is announced
          // by the skeleton rows below, not by a word in the empty search field.
          loading={isSearchMode && searching}
        />
      </div>

      {/* Rows (browse default list, or search results). Capped height + internal
          scroll so a long list scrolls inside the card instead of growing the
          page (which would push the list panel out of view) — matching the
          normal watchlist view. The header + search/tabs above stay fixed. */}
      <div className="mt-4 max-h-[calc(100vh-25rem)] divide-y divide-gray-100 overflow-y-auto">
        {rows.map((item, i) => {
          const d = displayFor(item);
          return (
            <div key={`${d.addSymbol}-${i}`} className="flex items-center gap-3 px-2 py-3">
              <button
                type="button"
                onClick={() => openChart(d.addSymbol)}
                aria-label={`Open ${d.chip} chart`}
                className="flex min-w-0 flex-1 items-center gap-3 text-left transition hover:opacity-80"
              >
                <AssetAvatar logo={d.logo} chip={d.chip} className="h-10 w-10" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-montserrat text-[15px] text-black" title={d.name}>
                    {d.name}
                  </div>
                </div>
              </button>
              <button
                type="button"
                aria-label={`Add ${d.chip}`}
                onClick={() => onAdd(d.addSymbol)}
                className="shrink-0 grayscale transition hover:grayscale-0"
              >
                {IconPlusCircle}
              </button>
            </div>
          );
        })}

        {/* First load (or a fresh search / tab switch) — placeholder rows on the
            same rhythm as the real ones, never an empty card. Appending via
            "Load more" keeps the rows already on screen and shows its own
            inline label instead. */}
        {loading && rows.length === 0 && (
          <ListSkeleton
            rows={BROWSE_LIMIT}
            avatarSize={40}
            columns={0}
            divided={false}
            rowClassName="px-2 py-3"
            label={isSearchMode ? "Searching" : "Loading assets"}
          />
        )}

        {!loading && rows.length === 0 && (
          <div className="py-12 text-center text-sm text-secondary">
            {isSearchMode ? "No matches found" : "No assets to show"}
          </div>
        )}
      </div>

      {/* Load more (browse only) */}
      {!isSearchMode && browseHasMore && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => loadBrowse(category, browsePage + 1)}
            disabled={browseLoading}
            className="text-sm font-medium text-primaryColor disabled:opacity-50"
          >
            {browseLoading ? "Loading" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
